<script>
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import axios from "axios";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

const MAX_FILE_SIZE_MB = 2;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ALLOWED_EXTENSIONS = [".xlsx", ".xls"];
const ALLOWED_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
];

export default {
  page: {
    title: "Import Soal",
    meta: [{ name: "description", content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
  },

  data() {
    return {
      title: "Import Soal",
      items: [
        { text: "Soal", href: "/" },
        { text: "Import Soal", active: true },
      ],

      // File state
      selectedFile: null,
      isDragOver: false,

      // Upload state
      isUploading: false,
      uploadProgress: 0,

      // Validation
      fileError: null,

      // Preview state
      previewData: [],
      previewHeaders: [],
      showPreview: false,
      isParsingFile: false,
      MAX_FILE_SIZE_MB: MAX_FILE_SIZE_MB,

      // Result state
      importResult: null, // { success: true/false, message, errors: [], stats: {} }
    };
  },

  computed: {
    idKompetensi() {
      return this.$route.params.id;
    },

    fileSizeDisplay() {
      if (!this.selectedFile) return "";
      const kb = this.selectedFile.size / 1024;
      if (kb < 1024) return `${kb.toFixed(1)} KB`;
      return `${(kb / 1024).toFixed(2)} MB`;
    },

    isFileReady() {
      return this.selectedFile !== null && this.fileError === null;
    },
  },

  methods: {
    // ─── File Selection ───────────────────────────────────────────────────────

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    onFileInputChange(event) {
      const file = event.target.files[0];
      if (file) this.handleFileSelected(file);
      // Reset input agar bisa pilih file yang sama lagi
      event.target.value = "";
    },

    onDragOver(event) {
      event.preventDefault();
      this.isDragOver = true;
    },

    onDragLeave() {
      this.isDragOver = false;
    },

    onDrop(event) {
      event.preventDefault();
      this.isDragOver = false;
      const file = event.dataTransfer.files[0];
      if (file) this.handleFileSelected(file);
    },

    handleFileSelected(file) {
      // Reset state sebelumnya
      this.resetState();

      // Validasi
      const validationError = this.validateFile(file);
      if (validationError) {
        this.fileError = validationError;
        return;
      }

      this.selectedFile = file;
      this.parseExcelPreview(file);
    },

    // ─── Validation ───────────────────────────────────────────────────────────

    validateFile(file) {
      if (!file) return "Tidak ada file yang dipilih.";

      const extension = "." + file.name.split(".").pop().toLowerCase();
      const isValidExtension = ALLOWED_EXTENSIONS.includes(extension);
      const isValidMime =
        ALLOWED_MIME_TYPES.includes(file.type) || file.type === "";

      if (!isValidExtension || !isValidMime) {
        return `Format file tidak didukung. Hanya menerima file Excel (${ALLOWED_EXTENSIONS.join(
          ", ",
        )}).`;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        return `Ukuran file melebihi batas maksimal ${MAX_FILE_SIZE_MB}MB. Ukuran file Anda: ${(
          file.size /
          1024 /
          1024
        ).toFixed(2)}MB.`;
      }

      return null;
    },

    // ─── Excel Preview ────────────────────────────────────────────────────────

    async parseExcelPreview(file) {
      this.isParsingFile = true;
      this.showPreview = false;

      try {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Ambil max 10 baris untuk preview
        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        });

        if (jsonData.length === 0) {
          this.fileError = "File Excel kosong atau tidak memiliki data.";
          this.selectedFile = null;
          return;
        }

        this.previewHeaders = jsonData[0];
        this.previewData = jsonData.slice(1, 11); // max 10 baris data
        this.showPreview = true;
      } catch (err) {
        console.error("Error parsing Excel:", err);
        this.fileError =
          "Gagal membaca file Excel. Pastikan file tidak rusak atau terenkripsi.";
        this.selectedFile = null;
      } finally {
        this.isParsingFile = false;
      }

      // Langsung upload setelah preview berhasil di-parse
      if (this.selectedFile) {
        await this.uploadFile();
      }
    },

    // ─── Upload ───────────────────────────────────────────────────────────────

    async uploadFile() {
      if (!this.isFileReady || this.isUploading) return;

      this.isUploading = true;
      this.uploadProgress = 0;
      this.importResult = null;

      const formData = new FormData();
      formData.append("file", this.selectedFile);
      formData.append("id_kompetensi_level", this.idKompetensi);
      if (this.$route.query.level) {
        formData.append("level", this.$route.query.level);
      }

      try {
        const response = await axios({
          method: "post",
          url:
            process.env.VUE_APP_BACKEND_URL_VERSION +
            `verifikasi/kompetensi-level/${this.idKompetensi}/soal/import`,
          params: this.$route.query.level
            ? { level: this.$route.query.level }
            : {},
          data: formData,
          headers: {
            Authorization: `Bearer ${localStorage.access_token}`,
            Accept: "application/json",
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              this.uploadProgress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
            }
          },
        });

        const resData = response.data;
        const stats = resData?.data || null;
        const rowErrors = (stats?.errors || []).map((e) => ({
          field: e.row ? `Baris ${e.row}` : null,
          message: e.message,
        }));

        const hasFailedRows = (stats?.total_gagal || 0) > 0;

        this.importResult = {
          success: !hasFailedRows,
          message:
            resData?.meta?.message ||
            resData?.message ||
            (hasFailedRows
              ? "Import soal selesai, namun ada baris yang gagal diproses."
              : "Import soal berhasil!"),
          stats,
          errors: rowErrors,
        };

        Swal.fire({
          icon: hasFailedRows ? "warning" : "success",
          title: hasFailedRows ? "Import Selesai dengan Catatan" : "Import Berhasil!",
          html: hasFailedRows
            ? `${this.importResult.message}<br><small>${stats.total_sukses} berhasil, ${stats.total_gagal} gagal.</small>`
            : this.importResult.message,
          confirmButtonColor: hasFailedRows ? "#f1b44c" : "#34c38f",
        });
      } catch (error) {
        const status = error.response?.status;
        const resData = error.response?.data;

        let errorMessage =
          resData?.meta?.message ||
          resData?.message ||
          "Terjadi kesalahan saat mengimport soal.";

        // Handling Laravel validation errors (422)
        let validationErrors = [];
        if (status === 422 && resData?.errors) {
          validationErrors = Object.entries(resData.errors).flatMap(
            ([field, messages]) =>
              messages.map((msg) => ({ field, message: msg })),
          );
        } else if (status === 422 && resData?.data?.errors) {
          // Format errors berupa array dari backend custom
          validationErrors = resData.data.errors;
        }

        this.importResult = {
          success: false,
          message: errorMessage,
          errors: validationErrors,
          stats: null,
        };

        Swal.fire({
          icon: "error",
          title: "Import Gagal",
          text: errorMessage,
          confirmButtonColor: "#f46a6a",
        });
      } finally {
        this.isUploading = false;
      }
    },

    // ─── Helpers ──────────────────────────────────────────────────────────────

    downloadTemplate() {
      const levelParam = this.$route.query.level
        ? `?level=${this.$route.query.level}`
        : "";
      window.open(
        process.env.VUE_APP_BACKEND_URL_VERSION +
          "download-template/kompetensi-level/" +
          this.idKompetensi +
          levelParam,
        "_blank",
      );
    },

    resetState() {
      this.selectedFile = null;
      this.fileError = null;
      this.uploadProgress = 0;
      this.isUploading = false;
      this.previewData = [];
      this.previewHeaders = [];
      this.showPreview = false;
      this.importResult = null;
      this.isParsingFile = false;
    },

    removeFile() {
      this.resetState();
    },

    goBack() {
      this.$router.back();
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row">
      <div class="col-12">
        <div class="card">
          <div
            class="card-header d-flex align-items-center justify-content-between"
          >
            <h5 class="mb-0">
              <i class="fa fa-file-import me-2 text-primary"></i>
              Import Soal dari Excel
            </h5>
            <button class="btn btn-secondary btn-sm" @click="goBack">
              <i class="fa fa-arrow-left me-1"></i> Kembali
            </button>
          </div>

          <div class="card-body">
            <!-- ── INFO TEMPLATE ──────────────────────────────────────── -->
            <div class="alert alert-info d-flex align-items-start gap-3 mb-4">
              <i class="fa fa-circle-info fa-lg mt-1"></i>
              <div>
                <strong>Panduan Import Soal</strong>
                <ul class="mb-1 mt-1 ps-3">
                  <li>
                    Format file yang didukung: <code>.xlsx</code>,
                    <code>.xls</code>
                  </li>
                  <li>
                    Maksimal ukuran file:
                    <strong>{{ MAX_FILE_SIZE_MB }}MB</strong>
                  </li>
                  <li>
                    Pastikan file sesuai dengan format template yang telah
                    disediakan
                  </li>
                  <li>File akan diproses otomatis setelah dipilih</li>
                </ul>
                <a
                  href="#"
                  class="btn btn-outline-info btn-sm mt-1"
                  @click="downloadTemplate"
                >
                  <i class="fa fa-download me-1"></i> Download Template Excel
                </a>
              </div>
            </div>

            <!-- ── DROP ZONE ──────────────────────────────────────────── -->
            <div
              class="drop-zone"
              :class="{
                'drop-zone--active': isDragOver,
                'drop-zone--filled': selectedFile && !fileError,
                'drop-zone--error': fileError,
                'drop-zone--uploading': isUploading,
              }"
              @click="!isUploading && triggerFileInput()"
              @dragover="onDragOver"
              @dragleave="onDragLeave"
              @drop="onDrop"
            >
              <input
                ref="fileInput"
                type="file"
                accept=".xlsx,.xls"
                class="d-none"
                @change="onFileInputChange"
                :disabled="isUploading"
              />

              <!-- State: Idle / Drag -->
              <template v-if="!selectedFile && !isParsingFile">
                <div class="drop-zone__content">
                  <i
                    class="fa fa-cloud-arrow-up drop-zone__icon"
                    :class="isDragOver ? 'text-primary' : 'text-muted'"
                  ></i>
                  <p class="drop-zone__text mb-1">
                    <span v-if="isDragOver">Lepaskan file di sini</span>
                    <span v-else>
                      <strong>Klik untuk pilih file</strong> atau seret & lepas
                      ke sini
                    </span>
                  </p>
                  <p class="text-muted small mb-0">
                    Excel (.xlsx, .xls) — Maks. {{ MAX_FILE_SIZE_MB }}MB
                  </p>
                </div>
              </template>

              <!-- State: Parsing -->
              <template v-else-if="isParsingFile">
                <div class="drop-zone__content">
                  <i class="fa fa-spinner fa-spin fa-2x text-primary mb-2"></i>
                  <p class="mb-0">Membaca file...</p>
                </div>
              </template>

              <!-- State: File ready -->
              <template v-else-if="selectedFile">
                <div class="drop-zone__content">
                  <i class="fa fa-file-excel fa-3x text-success mb-2"></i>
                  <p class="fw-semibold mb-0">{{ selectedFile.name }}</p>
                  <p class="text-muted small mb-0">{{ fileSizeDisplay }}</p>
                </div>
              </template>
            </div>

            <!-- ── FILE ERROR ─────────────────────────────────────────── -->
            <div
              v-if="fileError"
              class="alert alert-danger mt-3 d-flex align-items-center gap-2"
            >
              <i class="fa fa-triangle-exclamation"></i>
              <span>{{ fileError }}</span>
              <button
                type="button"
                class="btn-close ms-auto"
                @click="resetState"
              ></button>
            </div>

            <!-- ── PROGRESS BAR ───────────────────────────────────────── -->
            <div v-if="isUploading" class="mt-3">
              <div class="d-flex justify-content-between mb-1">
                <small class="text-muted">
                  <i class="fa fa-spinner fa-spin me-1"></i>
                  Mengupload soal ke server...
                </small>
                <small class="fw-semibold">{{ uploadProgress }}%</small>
              </div>
              <div class="progress" style="height: 10px">
                <div
                  class="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                  :style="{ width: uploadProgress + '%' }"
                  role="progressbar"
                ></div>
              </div>
            </div>

            <!-- ── SELECTED FILE INFO + REMOVE ───────────────────────── -->
            <div
              v-if="selectedFile && !isUploading && !importResult"
              class="mt-3 d-flex align-items-center justify-content-between p-2 bg-light rounded"
            >
              <div class="d-flex align-items-center gap-2">
                <i class="fa fa-file-excel text-success"></i>
                <span class="small">{{ selectedFile.name }}</span>
                <span class="badge bg-secondary">{{ fileSizeDisplay }}</span>
              </div>
              <button class="btn btn-outline-danger btn-sm" @click="removeFile">
                <i class="fa fa-times me-1"></i> Hapus
              </button>
            </div>

            <!-- ── IMPORT RESULT ──────────────────────────────────────── -->
            <div v-if="importResult" class="mt-4">
              <!-- Sukses -->
              <div v-if="importResult.success" class="alert alert-success">
                <div class="d-flex align-items-center gap-2 mb-2">
                  <i class="fa fa-circle-check fa-lg"></i>
                  <strong>Import Berhasil!</strong>
                </div>
                <p class="mb-2">{{ importResult.message }}</p>

                <!-- Stats jika ada -->
                <div v-if="importResult.stats" class="row g-2 mt-1">
                  <div
                    v-for="(val, key) in importResult.stats"
                    :key="key"
                    class="col-auto"
                  >
                    <div class="badge bg-success fs-6 px-3 py-2">
                      {{ key }}: <strong>{{ val }}</strong>
                    </div>
                  </div>
                </div>

                <div class="mt-3 d-flex gap-2">
                  <!-- <button class="btn btn-success btn-sm" @click="goBack">
                    <i class="fa fa-arrow-left me-1"></i> Kembali ke Daftar Soal
                  </button> -->
                  <button
                    class="btn btn-outline-success btn-sm"
                    @click="resetState"
                  >
                    <i class="fa fa-plus me-1"></i> Import Lagi
                  </button>
                </div>
              </div>

              <!-- Gagal / Sebagian Gagal -->
              <div v-else class="alert alert-danger">
                <div class="d-flex align-items-center gap-2 mb-2">
                  <i class="fa fa-circle-xmark fa-lg"></i>
                  <strong>
                    {{
                      importResult.stats?.total_sukses
                        ? "Sebagian Baris Gagal Diimport"
                        : "Import Gagal"
                    }}
                  </strong>
                </div>
                <p class="mb-2">{{ importResult.message }}</p>
                <p v-if="importResult.stats" class="mb-2 small text-muted">
                  {{ importResult.stats.total_sukses }} baris berhasil,
                  {{ importResult.stats.total_gagal }} baris gagal dari
                  {{ importResult.stats.total_baris }} baris.
                </p>

                <!-- Validation errors -->
                <div
                  v-if="importResult.errors && importResult.errors.length"
                  class="mt-2"
                >
                  <p class="fw-semibold mb-1">Detail Error:</p>
                  <ul class="mb-0 ps-3">
                    <li
                      v-for="(err, idx) in importResult.errors"
                      :key="idx"
                      class="small"
                    >
                      <span v-if="err.field">
                        <code>{{ err.field }}</code
                        >:
                      </span>
                      {{ err.message || err }}
                    </li>
                  </ul>
                </div>

                <div class="mt-3">
                  <button
                    class="btn btn-outline-danger btn-sm"
                    @click="resetState"
                  >
                    <i class="fa fa-rotate me-1"></i> Coba Lagi
                  </button>
                </div>
              </div>
            </div>

            <!-- ── EXCEL PREVIEW TABLE ─────────────────────────────────── -->
            <!-- <div v-if="showPreview && previewHeaders.length" class="mt-4">
              <div
                class="d-flex align-items-center justify-content-between mb-2"
              >
                <h6 class="mb-0">
                  <i class="fa fa-table me-1 text-success"></i>
                  Preview Data ({{ previewData.length }} baris pertama)
                </h6>
                <span class="badge bg-light text-dark border">
                  {{ previewHeaders.length }} kolom terdeteksi
                </span>
              </div>

              <div class="table-responsive border rounded">
                <table class="table table-sm table-bordered table-hover mb-0">
                  <thead class="table-success">
                    <tr>
                      <th class="text-center" style="width: 40px">#</th>
                      <th
                        v-for="(header, idx) in previewHeaders"
                        :key="idx"
                        class="text-nowrap"
                      >
                        {{ header || `Kolom ${idx + 1}` }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!previewData.length">
                      <td
                        :colspan="previewHeaders.length + 1"
                        class="text-center text-muted"
                      >
                        Tidak ada data untuk ditampilkan
                      </td>
                    </tr>
                    <tr
                      v-else
                      v-for="(row, rowIdx) in previewData"
                      :key="rowIdx"
                    >
                      <td class="text-center text-muted">{{ rowIdx + 1 }}</td>
                      <td
                        v-for="(cell, cellIdx) in previewHeaders"
                        :key="cellIdx"
                        :title="String(row[cellIdx] ?? '')"
                      >
                        <span class="preview-cell">{{
                          row[cellIdx] ?? "-"
                        }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p class="text-muted small mt-1 mb-0">
                <i class="fa fa-circle-info me-1"></i>
                Menampilkan maks. 10 baris pertama sebagai preview.
              </p>
            </div> -->
          </div>
          <!-- /card-body -->
        </div>
        <!-- /card -->
      </div>
      <!-- /col -->
    </div>
    <!-- /row -->
  </Layout>
</template>

<style scoped>
/* ── Drop Zone ─────────────────────────────────────────────────────────────── */
.drop-zone {
  border: 2px dashed #ced4da;
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #fafafa;
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drop-zone:hover:not(.drop-zone--uploading) {
  border-color: #556ee6;
  background-color: #f0f3ff;
}

.drop-zone--active {
  border-color: #556ee6 !important;
  background-color: #eef1ff !important;
  transform: scale(1.01);
}

.drop-zone--filled {
  border-color: #34c38f;
  background-color: #f0fff8;
  cursor: default;
}

.drop-zone--error {
  border-color: #f46a6a;
  background-color: #fff5f5;
  cursor: pointer;
}

.drop-zone--uploading {
  cursor: not-allowed;
  opacity: 0.85;
  border-color: #556ee6;
  background-color: #f0f3ff;
}

.drop-zone__content {
  pointer-events: none;
}

.drop-zone__icon {
  font-size: 3rem;
  margin-bottom: 12px;
  display: block;
}

.drop-zone__text {
  font-size: 1rem;
  color: #495057;
}

/* ── Preview Table ─────────────────────────────────────────────────────────── */
.preview-cell {
  display: inline-block;
  max-width: 180px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  vertical-align: bottom;
}
</style>
